import React, { useState, useEffect } from 'react';
import { getBatchDataApi, updateBatchApi } from 'Services/allApi';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { MdUpload, MdDelete, MdAdd, MdEdit } from 'react-icons/md';
import { deleteNewBatchDateApi } from 'Services/allApi';

const NewBatch = () => {
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingIndex, setEditingIndex] = useState(null); 
  const [newIndexValue, setNewIndexValue] = useState("");

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await getBatchDataApi();
        if (response && response.status === 200 && Array.isArray(response.data)) {
          setCourses(response.data);
        } else {
          console.error("Unexpected response format or error:", response);
          toast.error("Failed to fetch data");
        }
      } catch (error) {
        console.error("Error fetching batch data:", error.message);
        toast.error("An error occurred while fetching batches.");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const handleInputChange = (courseIndex, batchIndex, event) => {
    const updatedCourses = [...courses];
    updatedCourses[courseIndex].batchs[batchIndex].start_date = event.target.value;
    setCourses(updatedCourses);
  };

  const validateBatches = (batches) => {
    for (const batch of batches) {
      if (!batch.location || !batch.start_date) {
        return false;
      }
    }
    return true;
  };

  const handleSaveCourse = async (courseIndex) => {
    const course = courses[courseIndex];
    
    // Prepare batches for update, handling both existing and new batches
    const batchesToUpdate = course.batchs.map((batch) => {
      // For existing batches (positive IDs), include the ID
      if (batch.id > 0) {
        return {
          id: batch.id,
          location: batch.location,
          start_date: batch.start_date,
          index: batch.index
        };
      } 
      // For new batches (negative/temporary IDs), omit the ID field
      else {
        return {
          // No id field for new batches
          location: batch.location,
          start_date: batch.start_date,
          index: batch.index
        };
      }
    });

    if (!validateBatches(course.batchs)) {
      toast.error("All fields must be filled in before saving.");
      return;
    }

    try {
      const courseId = course.course_id;

      // Send all batches to the update API
      const response = await updateBatchApi(courseId, batchesToUpdate);

      if (response.status === 207) {
        toast.success("All batches updated successfully!");
        
        // Refresh data to get server-generated IDs for new batches
        const refreshResponse = await getBatchDataApi();
        if (refreshResponse && refreshResponse.status === 200 && Array.isArray(refreshResponse.data)) {
          setCourses(refreshResponse.data);
        }
      } else {
        console.error("Update failed:", response);
        toast.error("Some batches could not be updated. Please check logs.");
      }
    } catch (error) {
      console.error("Error updating batches:", error.message);
      toast.error("An error occurred while updating batches.");
    }
  };

  // Delete a batch
  const handleDeleteBatch = (courseIndex, batchId) => {
    console.log(courseIndex, batchId);
    const updatedCourses = [...courses];
    const batchIndex = updatedCourses[courseIndex].batchs.findIndex(batch => batch.id === batchId);

    const response = deleteNewBatchDateApi(batchId);
    
    if (batchIndex !== -1) {
      updatedCourses[courseIndex].batchs.splice(batchIndex, 1);
      setCourses(updatedCourses);
    }
  };

  // Start editing an index
  const startEditingIndex = (batchId, currentIndex) => {
    setEditingIndex(batchId);
    setNewIndexValue(currentIndex !== null ? currentIndex.toString() : "");
  };

  // Handle index input change
  const handleIndexInputChange = (e) => {
    // Only allow numbers
    const value = e.target.value.replace(/[^0-9]/g, '');
    setNewIndexValue(value);
  };

  // Save the edited index with auto-adjustment for duplicates
 // Save the edited index with proper handling of duplicates
const saveIndexEdit = (courseIndex, batchId) => {
  const updatedCourses = [...courses];
  const course = updatedCourses[courseIndex];
  const batchIndex = course.batchs.findIndex(batch => batch.id === batchId);
  
  if (batchIndex !== -1) {
    // Convert to number or null if empty
    const newIndex = newIndexValue === "" ? null : parseInt(newIndexValue, 10);
    const batch = course.batchs[batchIndex];
    const oldIndex = batch.index;
    
    if (newIndex !== null) {
      // Find all batches from the same location
      const locationBatches = course.batchs.filter(b => 
        b.location === batch.location && b.id !== batchId
      );
      
      // Check if any batch already has this index
      const existingBatchWithIndex = locationBatches.find(b => b.index === newIndex);
      
      if (existingBatchWithIndex) {
        // Instead of incrementing all indices, just swap the indices
        existingBatchWithIndex.index = oldIndex;
      }
    }
    
    // Set the new index value for the current batch
    batch.index = newIndex;
    setCourses(updatedCourses);
    setEditingIndex(null);
    setNewIndexValue("");
    toast.info("Index updated. Changes will be saved when you click Save.");
  }
};

  // Cancel index editing
  const cancelIndexEdit = () => {
    setEditingIndex(null);
    setNewIndexValue("");
  };

  // Handle key press in index input field
  const handleIndexKeyPress = (e, courseIndex, batchId) => {
    if (e.key === 'Enter') {
      saveIndexEdit(courseIndex, batchId);
    } else if (e.key === 'Escape') {
      cancelIndexEdit();
    }
  };

  // Add a new batch for a specific location with automatic index
  // Add a new batch for a specific location with automatic highest index
const handleAddBatch = (courseIndex, locationId, locationName) => {
  const updatedCourses = [...courses];
  const course = updatedCourses[courseIndex];

  // Generate a temporary unique ID (negative timestamp to avoid conflicts)
  const tempId = -Date.now();
  
  // Find the highest index currently used for this location
  const locationBatches = course.batchs.filter(batch => batch.location === locationId);
  let highestIndex = 0;
  
  // Loop through batches to find the highest index
  locationBatches.forEach(batch => {
    if (batch.index !== null && batch.index > highestIndex) {
      highestIndex = batch.index;
    }
  });
  
  // New index will be one higher than the current highest
  const newIndex = highestIndex + 1;

  // Create a new batch with the new index
  const newBatch = {
    id: tempId,
    course: course.course_id,
    course_name: course.course,
    location: locationId,
    location_name: locationName,
    start_date: "New batch - Enter date",
    index: newIndex
  };

  // Add the new batch
  updatedCourses[courseIndex].batchs.push(newBatch);
  setCourses(updatedCourses);
  toast.info("New batch added with index " + newIndex + ". Fill in details and click Save.");
};

  // Group batches by location and sort them based on index if available
  const groupBatchesByLocation = (batches) => {
    const groupedBatches = {};
    
    batches.forEach(batch => {
      const locationKey = batch.location_name;
      if (!groupedBatches[locationKey]) {
        groupedBatches[locationKey] = [];
      }
      groupedBatches[locationKey].push(batch);
    });
    
    // Sort each location's batches by index if index is not null
    Object.keys(groupedBatches).forEach(location => {
      // First separate batches with and without indices
      const withIndices = groupedBatches[location].filter(batch => batch.index !== null);
      const withoutIndices = groupedBatches[location].filter(batch => batch.index === null);
      
      // Sort batches with indices
      withIndices.sort((a, b) => a.index - b.index);
      
      // Combine them back, with indexed batches first in order
      groupedBatches[location] = [...withIndices, ...withoutIndices];
    });
    
    return groupedBatches;
  };

  return (
    <div className="max-w-4xl mx-auto p-6 bg-white shadow-lg rounded-lg dark:!bg-navy-700 dark:text-white">
      <h2 className="text-2xl font-semibold mb-6 dark:text-white">Course Batches</h2>
      {loading ? (
        <p className="text-center text-purple-500 dark:text-purple-300">Loading courses... Please wait.</p>
      ) : courses.length === 0 ? (
        <p className="text-center text-red-500 dark:text-red-400">No courses found.</p>
      ) : (
        courses.map((course, courseIndex) => {
          const groupedBatches = groupBatchesByLocation(course.batchs);
          
          return (
            <div key={course.course_id} className="mb-6 p-6 border border-gray-200 rounded-lg shadow-md dark:border-gray-600">
              <h3 className="text-xl font-bold mb-4 dark:text-white">{course.course}</h3>
              
              {Object.entries(groupedBatches).map(([locationName, batches]) => (
                <div key={locationName} className="mb-6">
                  <div className="flex justify-between items-center mb-3">
                    <h4 className="text-lg font-semibold dark:text-gray-200">{locationName}</h4>
                    <button 
                      onClick={() => handleAddBatch(
                        courseIndex, 
                        batches[0].location, 
                        locationName
                      )}
                      className="flex items-center px-2 py-1 text-sm bg-blue-500 text-white rounded hover:bg-blue-600 dark:bg-blue-600 dark:hover:bg-blue-700"
                    >
                      <MdAdd className="mr-1" /> Add Batch
                    </button>
                  </div>
                  <div className="space-y-4">
                    {batches.map((batch) => {
                      // Find the actual index in the original array
                      const originalBatchIndex = course.batchs.findIndex(b => b.id === batch.id);
                      
                      return (
                        <div key={batch.id} className="flex items-center gap-4">
                          <div className="flex-grow">
                            <input
                              type="text"
                              id={`batch-${batch.id}`}
                              value={batch.start_date}
                              onChange={(e) => handleInputChange(courseIndex, originalBatchIndex, e)}
                              className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:bg-[#0b14374d] dark:text-white dark:border-gray-600 dark:focus:ring-indigo-400"
                              placeholder="Enter start date"
                            />
                          </div>
                          
                          {/* Index display and edit - no checkmark */}
                          <div className="min-w-24 flex items-center">
                            {editingIndex === batch.id ? (
                              <div className="flex items-center">
                                <input
                                  type="text"
                                  value={newIndexValue}
                                  onChange={handleIndexInputChange}
                                  onKeyDown={(e) => handleIndexKeyPress(e, courseIndex, batch.id)}
                                  onBlur={() => saveIndexEdit(courseIndex, batch.id)}
                                  className="w-12 px-2 py-1 text-center border border-gray-300 rounded-md dark:bg-[#0b14374d] dark:text-white dark:border-gray-600"
                                  autoFocus
                                />
                                <button
                                  onClick={cancelIndexEdit}
                                  className="p-1 text-red-500 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300 ml-1"
                                  title="Cancel"
                                >
                                  ✕
                                </button>
                              </div>
                            ) : (
                              <div className="flex items-center text-sm text-gray-500 dark:text-gray-400">
                                {batch.index !== null ? (
                                  <>
                                    <span className="mr-1">#{batch.index}</span>
                                    <button
                                      onClick={() => startEditingIndex(batch.id, batch.index)}
                                      className="p-1 text-blue-500 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300"
                                      title="Edit index"
                                    >
                                      <MdEdit size={16} />
                                    </button>
                                  </>
                                ) : (
                                  <button
                                    onClick={() => startEditingIndex(batch.id, null)}
                                    className="p-1 text-blue-500 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300"
                                    title="Add index"
                                  >
                                    <MdAdd size={16} /> Index
                                  </button>
                                )}
                              </div>
                            )}
                          </div>
                          
                          {/* Show delete button only if location has more than one batch */}
                          {batches.length > 1 && (
                            <button
                              onClick={() => handleDeleteBatch(courseIndex, batch.id)}
                              className="p-2 text-red-500 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300"
                              title="Delete batch"
                            >
                              <MdDelete size={20} />
                            </button>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}

              <button
                onClick={() => handleSaveCourse(courseIndex)}
                type="submit"
                className="rounded-md border-2 border-green-600 px-3 py-2 font-semibold text-green-600 shadow-sm hover:bg-green-100 dark:hover:bg-gray-700 focus-visible:outline focus-visible:outline-2 focus-visible:outline-green-600 flex items-center dark:border-green-400 dark:text-green-400"
              >
                <MdUpload className="mr-2" /> Save
              </button>
            </div>
          );
        })
      )}
      <ToastContainer position="top-center" autoClose={5000} hideProgressBar={false} newestOnTop closeButton={true} />
    </div>
  );
};

export default NewBatch;